import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, Mail, MessageCircle, MapPin, ArrowRight, Loader2,
  Inbox, CheckCircle2, Archive, Clock, AlertTriangle, Copy, Send,
} from "lucide-react";
import { adminApi } from "../../lib/api";
import { openWhatsApp } from "../../lib/whatsapp";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUOTE_TABS = [
  { value: "new", label: "New", icon: Inbox, color: "#f28a16" },
  { value: "contacted", label: "Contacted", icon: Phone, color: "#3b82f6" },
  { value: "converted", label: "Converted", icon: CheckCircle2, color: "#10b981" },
  { value: "archived", label: "Archived", icon: Archive, color: "#6b7280" },
];

const daysSince = (iso) => {
  const d = new Date(iso);
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

const validityPill = (quote) => {
  if (quote.status === "converted") {
    return { text: "Converted", color: "#10b981", icon: CheckCircle2 };
  }
  if (quote.status === "archived") {
    return { text: "Archived", color: "#6b7280", icon: Archive };
  }
  const age = daysSince(quote.created_at);
  const remaining = 7 - age;
  if (remaining < 0) {
    return { text: `Expired ${-remaining}d ago`, color: "#dc2626", icon: AlertTriangle };
  }
  if (remaining === 0) {
    return { text: "Expires today", color: "#f28a16", icon: AlertTriangle };
  }
  return {
    text: `${remaining}d to expiry`,
    color: remaining <= 2 ? "#f28a16" : "#10b981",
    icon: Clock,
  };
};

export const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("new");
  const [busyId, setBusyId] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listQuotes();
      setQuotes(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await adminApi.updateQuoteStatus(id, status);
      toast.success(`Quote ${status}`);
      load();
    } catch {
      toast.error("Could not update");
    } finally { setBusyId(null); }
  };

  const convert = async (id) => {
    setBusyId(id);
    try {
      const job = await adminApi.convertQuoteToJob(id);
      toast.success(`Created Job #${job.job_number}`);
      nav(`/admin/jobs/${job.id}/edit`);
    } catch (e) {
      toast.error("Could not convert");
    } finally { setBusyId(null); }
  };

  const sendClientLink = async (q) => {
    setBusyId(q.id);
    try {
      const to = q.contact_email;
      const send = !!to && window.confirm(
        to
          ? `Send a secure client link to ${to}?`
          : "This quote has no email — create a link you can copy manually?"
      );
      // If user cancels the send but we have an email, they may still want just the link — abort.
      if (to && !send) { setBusyId(null); return; }
      const res = await adminApi.createQuoteClientLink(q.id, { send_email: !!to });
      const url = res.portal_url || `${window.location.origin}/portal/${res.token}`;
      if (res.email_result?.ok) {
        toast.success(`Link emailed to ${to}`);
      } else if (res.email_result && res.email_result.ok === false) {
        toast.error(`Email failed: ${res.email_result.error || "unknown error"}`);
      }
      try {
        await navigator.clipboard.writeText(url);
        toast.message("Portal URL copied to clipboard");
      } catch {
        window.prompt("Copy the client link:", url);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create link");
    } finally { setBusyId(null); }
  };

  const filtered = quotes.filter((q) => q.status === tab);
  const counts = QUOTE_TABS.reduce((m, t) => {
    m[t.value] = quotes.filter((q) => q.status === t.value).length;
    return m;
  }, {});

  return (
    <div data-testid="admin-quotes-page">
      <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Quote Inbox</h1>
      <p className="text-[#3f4b52] mt-1">
        Online quote requests. <b>7-day validity</b> per T&amp;Cs — each quote shows how many days it has left before it expires.
      </p>

      {/* Tabs */}
      <div className="mt-6 flex flex-wrap gap-2 border-b border-[#cfd7db]">
        {QUOTE_TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.value;
          return (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              data-testid={`quote-tab-${t.value}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 font-display uppercase tracking-wider text-xs border-b-2 transition ${
                active ? "border-[#f28a16] text-[#f28a16]" : "border-transparent text-[#3f4b52] hover:text-[#111619]"
              }`}
            >
              <Icon size={14} /> {t.label}
              <span className="bg-[#edf2f4] text-[#3f4b52] px-1.5 rounded-sm text-xs">{counts[t.value] || 0}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 space-y-3">
        {loading && <div className="text-sm text-[#3f4b52]">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-[#cfd7db] rounded-md p-10 text-center text-[#3f4b52]">
            No quotes in this tab.
          </div>
        )}
        {filtered.map((q) => {
          const pill = validityPill(q);
          const PillIcon = pill.icon;
          const phoneCleaned = (q.contact_phone || "").replace(/[^0-9]/g, "");
          return (
            <div key={q.id} className="bg-white border border-[#cfd7db] rounded-md p-5 shadow-sm" data-testid={`quote-row-${q.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-bold text-lg text-[#111619]">
                      {q.name} {q.surname}
                    </div>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-display uppercase tracking-widest text-white"
                      style={{ background: pill.color }}
                    >
                      <PillIcon size={11} /> {pill.text}
                    </span>
                  </div>
                  <div className="text-sm text-[#3f4b52]">
                    {q.vehicle_make} {q.vehicle_model} · <b className="text-[#111619]">{q.registration_number}</b>
                    {q.insurance && <span> · {q.insurance}</span>}
                  </div>
                  <div className="text-xs text-[#3f4b52] mt-1">
                    Submitted {new Date(q.created_at).toLocaleString("en-ZA")} · {daysSince(q.created_at)}d ago
                  </div>
                </div>
                <div className="text-xs text-[#3f4b52] text-right">
                  <div className="flex items-center gap-1.5 justify-end">
                    <MapPin size={12} className="text-[#f28a16]" /> {q.location || "—"}
                  </div>
                </div>
              </div>

              <div className="mt-3 bg-[#f5f6f6] rounded-md p-3 text-sm text-[#111619]">
                <div className="text-xs uppercase tracking-widest text-[#3f4b52] mb-1">Damage description</div>
                {q.damage_description || "—"}
              </div>

              {(q.broker_name || q.policy_number) && (
                <div className="mt-2 text-xs text-[#3f4b52]">
                  {q.broker_name && <>Broker: <b>{q.broker_name}</b>{q.broker_contact && ` (${q.broker_contact})`} · </>}
                  {q.policy_number && <>Policy: <b>{q.policy_number}</b></>}
                </div>
              )}

              {/* Action row */}
              <div className="mt-4 flex flex-wrap gap-2">
                {phoneCleaned && (
                  <a
                    href={`tel:${q.contact_phone}`}
                    data-testid={`quote-call-${q.id}`}
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-white border border-[#cfd7db] text-xs font-display uppercase tracking-wider text-[#111619] hover:border-[#f28a16] hover:text-[#f28a16]"
                  >
                    <Phone size={12} /> Call
                  </a>
                )}
                {phoneCleaned && (
                  <button
                    type="button"
                    onClick={() => openWhatsApp(q.contact_phone,
                      `Hi ${q.name || "there"}, this is Diesel & Dust regarding your windscreen quote for ${q.vehicle_make || ""} ${q.vehicle_model || ""} (${q.registration_number || "—"}).`)}
                    data-testid={`quote-whatsapp-${q.id}`}
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#25D366] text-white text-xs font-display uppercase tracking-wider hover:brightness-110"
                  >
                    <MessageCircle size={12} /> WhatsApp
                  </button>
                )}
                {q.contact_email && (
                  <a
                    href={`mailto:${q.contact_email}?subject=${encodeURIComponent("Your Diesel & Dust windscreen quote")}`}
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-white border border-[#cfd7db] text-xs font-display uppercase tracking-wider text-[#111619] hover:border-[#f28a16] hover:text-[#f28a16]"
                  >
                    <Mail size={12} /> Email
                  </a>
                )}
                <div className="flex-1" />
                {q.status !== "converted" && q.status !== "archived" && (
                  <button
                    onClick={() => sendClientLink(q)}
                    disabled={busyId === q.id}
                    data-testid={`quote-client-link-${q.id}`}
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#10b981] text-white text-xs font-display uppercase tracking-wider hover:brightness-110"
                    title={q.contact_email ? `Send secure link to ${q.contact_email}` : "Create secure link (copy manually)"}
                  >
                    {q.contact_email ? <Send size={12} /> : <Copy size={12} />} Client link
                  </button>
                )}
                {q.status === "new" && (
                  <button
                    onClick={() => setStatus(q.id, "contacted")}
                    disabled={busyId === q.id}
                    data-testid={`quote-mark-contacted-${q.id}`}
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#20282c] text-white text-xs font-display uppercase tracking-wider hover:brightness-110"
                  >
                    Mark contacted
                  </button>
                )}
                {q.status !== "converted" && q.status !== "archived" && (
                  <button
                    onClick={() => setStatus(q.id, "archived")}
                    disabled={busyId === q.id}
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-[#cfd7db] text-xs font-display uppercase tracking-wider text-[#3f4b52] hover:border-[#dc2626] hover:text-[#dc2626]"
                  >
                    <Archive size={12} /> Archive
                  </button>
                )}
                {q.status === "converted" && q.converted_job_id ? (
                  <Link
                    to={`/admin/jobs/${q.converted_job_id}`}
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#10b981] text-white text-xs font-display uppercase tracking-wider hover:brightness-110"
                  >
                    Open Job <ArrowRight size={12} />
                  </Link>
                ) : q.status !== "archived" && (
                  <>
                    <button
                      onClick={() => convert(q.id)}
                      disabled={busyId === q.id}
                      data-testid={`quote-convert-${q.id}`}
                      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#f28a16] text-white text-xs font-display uppercase tracking-wider hover:brightness-110 shadow"
                    >
                      {busyId === q.id ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
                      Convert to Job
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default AdminQuotes;
